import React from 'react';
import { Row, Col, Card, Statistic } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import type { DashboardStatistics } from '@/services/dashboard.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';

export interface ExecutiveKpiRowProps {
  statistics: DashboardStatistics | null;
}

export const ExecutiveKpiRow: React.FC<ExecutiveKpiRowProps> = ({ statistics }) => {
  const { formatCurrency, baseCurrency } = useCurrency();

  if (!statistics) return null;

  const trendIcon = (pct: number) =>
    pct >= 0 ? (
      <span style={{ fontSize: 13, color: '#389e0d' }}>
        <ArrowUpOutlined /> {pct}%
      </span>
    ) : (
      <span style={{ fontSize: 13, color: '#cf1322' }}>
        <ArrowDownOutlined /> {Math.abs(pct)}%
      </span>
    );

  const kpiCard = (title: string, value: string | number, suffix?: React.ReactNode) => (
    <Card
      size="small"
      style={{
        borderRadius: 8,
        border: EnterpriseTokens.workspace.borderSubtle,
        background: EnterpriseTokens.workspace.surfaceHeader,
      }}
      styles={{ body: { padding: 16 } }}
    >
      <Statistic title={title} value={value} suffix={suffix} />
    </Card>
  );

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: EnterpriseTokens.spacing.xl }}>
      <Col xs={24} sm={12} lg={6}>
        {kpiCard(
          'Revenue (YTD / snapshot)',
          formatCurrency(statistics.totalRevenue ?? 0),
          trendIcon(statistics.revenueTrend ?? 0)
        )}
      </Col>
      <Col xs={24} sm={12} lg={6}>
        {kpiCard('Open customer orders', statistics.salesOrders ?? 0, trendIcon(statistics.ordersTrend ?? 0))}
      </Col>
      <Col xs={24} sm={12} lg={6}>
        {kpiCard('Active projects', statistics.activeProjects ?? 0, trendIcon(statistics.projectsTrend ?? 0))}
      </Col>
      <Col xs={24} sm={12} lg={6}>
        {kpiCard(
          'Collection rate (paid / invoiced)',
          `${statistics.profitMargin ?? 0}%`,
          trendIcon(statistics.profitTrend ?? 0)
        )}
      </Col>
    </Row>
  );
};
